import * as React from 'react';
import * as styles from './client-tags.module.scss';
import TagTable from './tagTable';
import useTag from '../../../../hooks/useTag';
import Tag from '../../../../models/tag';
import PriorityTwoFailure from '../../../announcement/priority-two-failure';
import PriorityTwoSuccess from '../../../announcement/priority-two-success';
import { CircularProgress } from '@material-ui/core';
import AddTag from './addTag';
import useTimedAlerts from '../../../../hooks/useTimedAlerts';
import Administrator from '../../../../models/administrator';
import { AssignedUser } from '../../../../models/client';

interface Props {
  clientId: number;
  assignedUsers: AssignedUser[];
  assignedAdministrators: Administrator[];
}

const ClientTags = ({ clientId, assignedUsers, assignedAdministrators }: Props) => {
  const [clientTags, setClientTags] = React.useState<Tag[]>([]);
  const [loading, setLoading] = React.useState<boolean>(true);
  const { fetchTags, createTag, deleteTag } = useTag();
  const { successMessage, setSuccessMessage, errorMessage, setErrorMessage } = useTimedAlerts();
  const sortedTags = Array.from(clientTags);

  // order tags alphabetically for display
  sortedTags.sort((a, b) => {
    if (a.name.toUpperCase() < b.name.toUpperCase()) {
      return -1;
    }
    if (a.name.toUpperCase() > b.name.toUpperCase()) {
      return 1;
    }
    return 0;
  });

  const fetchTagsHandler = () => {
    if (!loading) setLoading(true);
    fetchTags(clientId)
      .then(tags => {
        if (tags) {
          setClientTags(tags);
        }
        setLoading(false);
      })
      .catch(err => {
        setErrorMessage(err.responseJSON.message);
      });
  };

  React.useEffect(() => {
    fetchTagsHandler();
  }, []);

  const addTag = (tagName: string) => {
    createTag(clientId, tagName)
      .then(response => {
        if (response.success) {
          setSuccessMessage(`Success: Added tag ${response.message}: ${tagName}`);
          fetchTagsHandler();
        } else if (!response.success) {
          setErrorMessage(response.message);
        }
      })
      .catch(err => {
        setErrorMessage(err.responseJSON.message);
      });
  };

  const deleteTagHandler = (tagId: number, tagName: string) => {
    deleteTag(clientId, tagId)
      .then(response => {
        if (response.success) {
          setSuccessMessage(`Success: Removed tag ${response.message}: ${tagName}`);
          fetchTagsHandler();
        } else if (!response.success) {
          setErrorMessage(response.message);
        }
      })
      .catch(err => {
        setErrorMessage(err.responseJSON.message);
      });
  };

  return (
    <div className={styles.tags}>
      {errorMessage && <PriorityTwoFailure message={errorMessage} />}
      {successMessage && <PriorityTwoSuccess message={successMessage} />}
      <AddTag addTagHandler={addTag} />
      <div className={styles.tagListWrap}>
        {loading && sortedTags.length === 0 ? (
          <div className="loadingIndicator">
            <CircularProgress />
          </div>
        ) : (
          <TagTable
            tags={sortedTags}
            deleteTagHandler={deleteTagHandler}
            clientUsers={assignedUsers}
            clientAdministrators={assignedAdministrators}
            clientId={clientId}
            setSuccessMessage={setSuccessMessage}
            setErrorMessage={setErrorMessage}
            fetchTagsHandler={fetchTagsHandler}
          />
        )}
      </div>
    </div>
  );
};

export default ClientTags;
